import React from "react";
import { RuleType } from "react-querybuilder";
import {
    buildDimensionOptions,
    transformDimensionFiltersToQueryBuilderRules,
    transformAttributeFiltersToQueryBuilderRules,
    transformQueryBuilderRulesToAttributeFilters,
    transformQueryBuilderRulesToDimensionFilters,
} from "../utils";
import { ISchemaKey } from "helpers/product-collection";
import { CollectionRefs } from "components/product-collection/product-collection-views/product-collection-grid-view/tool-panels/dimensions-tool-panel/dimensions-tool-panel";
import { FiltersQueryBuilder } from "components/common";
import { IAllocationDimension, transformDoorAttributesFiltersForRollup } from "helpers/allocation-dimension";
import { IDimensionOption, IFilters } from "../types";
import { ATTRIBUTES } from "../constants";

interface IProps {
    allocationDimensions: IAllocationDimension[];
    allocationDimensionAttributes: ISchemaKey[];
    appliedFilters: IFilters;
    collectionsRefs: CollectionRefs;
    onFiltersChange: (filters: IFilters) => void;
    onRequestClose: () => void;
    isServerRollup: boolean;
    collectionId?: string;
}

export const FiltersContent: React.FC<IProps> = ({
    allocationDimensions = [],
    allocationDimensionAttributes = [],
    appliedFilters,
    collectionsRefs,
    onFiltersChange,
    onRequestClose,
    isServerRollup,
    collectionId,
}) => {
    const { attributeFilters, dimensionFilters } = appliedFilters;
    const updatedAttributeFilters = isServerRollup
        ? transformDoorAttributesFiltersForRollup(collectionId, attributeFilters, allocationDimensionAttributes)
        : attributeFilters;

    const fields: IDimensionOption[] = React.useMemo(
        () =>
            buildDimensionOptions({
                allocationDimensions,
                dimensionFilters,
                allocationDimensionAttributes,
                collectionsRefs,
                isServerRollup,
            }),
        [allocationDimensions, dimensionFilters, allocationDimensionAttributes, collectionsRefs, isServerRollup]
    );
    const initialRules: RuleType[] = React.useMemo(
        () => [
            ...transformDimensionFiltersToQueryBuilderRules(allocationDimensions, dimensionFilters),
            ...transformAttributeFiltersToQueryBuilderRules(updatedAttributeFilters),
        ],
        [allocationDimensions, dimensionFilters, updatedAttributeFilters]
    );

    const handleApplyFilters = React.useCallback(
        (rules: RuleType[]) => {
            const dimensionRules = [];
            const attributeRules = [];
            rules.forEach((rule) => {
                if (rule.field !== ATTRIBUTES) {
                    dimensionRules.push(rule);
                } else {
                    attributeRules.push(rule);
                }
            });

            onFiltersChange({
                dimensionFilters: transformQueryBuilderRulesToDimensionFilters(allocationDimensions, dimensionRules),
                attributeFilters: transformQueryBuilderRulesToAttributeFilters(attributeRules),
            });
        },
        [allocationDimensions, onFiltersChange]
    );

    return (
        <FiltersQueryBuilder
            onApply={handleApplyFilters}
            onRequestClose={onRequestClose}
            fields={fields}
            initialRules={initialRules}
        />
    );
};
